import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useQuery } from "../../../hooks/useQuery";
import { Button } from "../../Button";
import { Text } from "../../Text";
import { DangerSection } from "../DangerSection";
import {
  PanelCard,
  PanelContentBox,
  PanelHeader,
  PanelShell,
} from "../SettingsPanel";
import { ExistingSsoProvider } from "./ExistingSsoProvider";
import { NewSsoProvider } from "./NewSsoProvider";
import { RevokeSessionsModal, ScimTokenModal } from "./SsoModals";

const SessionSection = styled(PanelContentBox)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export function LegacySSOSettings() {
  const z = useZero();
  const [providers] = useQuery(queries.ssoProviders());
  const canManageSSO = useHasAccess("tenant:sso_manage");
  const provider = providers[0];
  // Lives here so a token minted while registering a provider survives the
  // swap from NewSsoProvider to ExistingSsoProvider.
  const [scimToken, setScimToken] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  if (!canManageSSO) {
    return (
      <PanelShell>
        <PanelCard>
          <PanelHeader>
            <Text variant="display-md">Single sign-on</Text>
            <div style={{ marginTop: 8 }}>
              <Text variant="text-regular" color="var(--text-secondary)">
                Only admins can configure SSO
              </Text>
            </div>
          </PanelHeader>
        </PanelCard>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <PanelCard>
        <PanelHeader>
          <Text variant="display-md">Single sign-on</Text>
          <div style={{ marginTop: 8 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Configure single sign-on for your tenant. Group mappings sync
              roles and space memberships on each sign-in.
            </Text>
          </div>
        </PanelHeader>

        <PanelContentBox>
          {provider ? (
            <ExistingSsoProvider
              provider={provider}
              onGeneratedScimToken={setScimToken}
            />
          ) : (
            <NewSsoProvider onGeneratedScimToken={setScimToken} />
          )}
        </PanelContentBox>

        <SessionSection>
          <div>
            <Text variant="text-regular-md">Sessions</Text>
            <div style={{ marginTop: 4 }}>
              <Text variant="text-regular" color="var(--text-secondary)">
                Force all users to re-authenticate
              </Text>
            </div>
          </div>
          <Button
            variant="subtle"
            size="l"
            onClick={() => setConfirmRevoke(true)}
          >
            Revoke all sessions
          </Button>
        </SessionSection>

        {provider && (
          <DangerSection
            style={{ width: "100%", maxWidth: 648, marginTop: 24 }}
            title="Delete SSO provider"
            description="Users will no longer be able to sign in with this SSO provider. This can’t be undone."
            actionLabel="Delete SSO provider"
            confirm={{
              confirmLabel: "Delete",
              onConfirm: () => {
                z.mutate(sharedMutators.deleteSSOProvider({ id: provider.id }));
                setScimToken(null);
              },
            }}
          />
        )}
        <ScimTokenModal token={scimToken} onClose={() => setScimToken(null)} />
        <RevokeSessionsModal
          open={confirmRevoke}
          onClose={() => setConfirmRevoke(false)}
        />
      </PanelCard>
    </PanelShell>
  );
}
